import type { RendererProps } from "../core/element-registry";
import { useRenderContext } from "../core/render-context";
import { useAtomDefaults } from "../contexts/atom-defaults-context";
import { ElementRenderer } from "./element-renderer";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { stripPresetConflicts } from "../lib/preset-utils";
import { resolveIcon } from "./resolve-icon";
import {
  resolveColorToken,
  resolveStyleTokens,
  type RendererDesignTokens,
} from "../lib/style-token-resolver";
import {
  isSourceDerivedElement,
  sourceFillBoxStyle,
} from "./source-derived-rendering";
import {
  PARENT_ACTIVATION_HOST_CLASSES,
  ParentActivationHostContext,
  ParentActivationProxy,
  parentActivationHostStyle,
  resolveParentActivationLabel,
  subtreeContainsInteractiveControl,
} from "./parent-activation";

export function ButtonRenderer({ element }: RendererProps) {
  const {
    resolveSymbol,
    dispatchEvent,
    itemContext,
    mode,
    designTokens,
    hasEventBinding,
  } = useRenderContext();
  const options = element.options as Record<string, unknown> | undefined;
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("button");
  const text = element.text
    ? String(resolveSymbol(element.text) ?? "")
    : undefined;
  const rawAriaLabel = element.ariaLabel ?? options?.ariaLabel;
  const ariaLabel =
    typeof rawAriaLabel === "string"
      ? String(resolveSymbol(rawAriaLabel) ?? rawAriaLabel)
      : undefined;
  // Check if we have children (from rehype pipeline: button → span → text)
  const hasChildren = !text && element.children && element.children.length > 0;

  // Resolve dynamic icon names: #{state.isPlaying ? 'pause' : 'play'} → "pause"
  // Accept `name` as a fallback — many commerce-admin blocks were generated with
  // `"name": "chevron-left"` instead of the canonical `"icon"` field. Treat it as
  // the icon slot for button elements; real HTML form name attributes are not used
  // on buttons in Cogo blocks (verified across all deployed apps).
  const rawIcon = element.icon ?? (element as { name?: string }).name ?? "";
  const rawIconStr = typeof rawIcon === "string" ? rawIcon : "";
  const iconName = rawIconStr.includes("#{")
    ? String(resolveSymbol(rawIconStr) ?? "")
    : rawIconStr;
  const IconComponent = iconName ? resolveIcon(iconName) : undefined;

  // Valid variant/size sets — Normalizer guarantees these values for normalized blocks.
  // Non-normalized blocks get "default" fallback.
  type ButtonVariant =
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "none";
  type ButtonSize =
    | "default"
    | "xs"
    | "sm"
    | "lg"
    | "icon"
    | "icon-xs"
    | "icon-sm"
    | "icon-lg";
  const VALID_VARIANTS: ReadonlySet<string> = new Set([
    "default",
    "destructive",
    "outline",
    "secondary",
    "ghost",
    "link",
    "none",
  ]);
  const VALID_SIZES: ReadonlySet<string> = new Set([
    "default",
    "xs",
    "sm",
    "lg",
    "icon",
    "icon-xs",
    "icon-sm",
    "icon-lg",
  ]);

  // Resolve dynamic variant expressions like #{ui.selectedDateRange === '1month' ? 'default' : 'outline'}
  const variantStr = typeof element.variant === "string" ? element.variant : "";
  const rawVariant = variantStr
    ? variantStr.includes("#{")
      ? String(resolveSymbol(variantStr) ?? "")
      : variantStr
    : (atomDefaults.defaultVariant ?? "default");
  const variant: ButtonVariant = VALID_VARIANTS.has(rawVariant)
    ? (rawVariant as ButtonVariant)
    : "default";
  const rawSize = element.size ?? atomDefaults.defaultSize ?? "default";
  const size: ButtonSize = VALID_SIZES.has(rawSize)
    ? (rawSize as ButtonSize)
    : "default";
  // `md` is the ontology spelling for the shadcn default button size. Keep
  // the actual component size normalized while preserving the authored value
  // as a semantic readback for cross-runtime verification.
  const dataSize = rawSize === "md" ? "md" : size;
  const rawTone = (element as unknown as { tone?: unknown }).tone;
  const tone =
    typeof rawTone === "string"
      ? String(resolveSymbol(rawTone) ?? rawTone).toLowerCase()
      : "default";
  const toneClassName = buttonToneClassName(tone);
  const htmlType = resolveHtmlButtonType(element);
  const navigationTarget = typeof element.target === "string"
    ? String(resolveSymbol(element.target) ?? element.target)
    : undefined;

  const handleClick = (e: React.MouseEvent) => {
    if (mode === "edit" || htmlType === "button") e.preventDefault();
    if (mode !== "edit") e.stopPropagation();
    // Always dispatch tap — both inline events and external bindings (events.cogo.json) are handled
    // by the event dispatcher. itemContext enables iteration variable resolution (e.g., #{product.id})
    dispatchEvent(
      element.id,
      "tap",
      navigationTarget
        ? { ...(itemContext ?? {}), target: navigationTarget }
        : itemContext,
    );
  };

  // Resolve disabled/loading state from @{...} or #{...} expressions
  const rawDisabled = element.disabled;
  const isDisabled =
    typeof rawDisabled === "string"
      ? !!resolveSymbol(rawDisabled)
      : !!rawDisabled;
  const rawLoading = element.loading;
  const isLoadingBtn =
    typeof rawLoading === "string" ? !!resolveSymbol(rawLoading) : !!rawLoading;
  const sourceDataAttrs = dataAttributesFor(element);
  const rawDataState = (element as unknown as { dataState?: string }).dataState;
  const rawDataActive = (
    element as unknown as { dataActive?: boolean | string }
  ).dataActive;
  if (rawDataState !== undefined) sourceDataAttrs["data-state"] = rawDataState;
  if (rawDataActive !== undefined)
    sourceDataAttrs["data-active"] = rawDataActive;

  const extraClasses = cn(
    element.fill && "w-full",
    toneClassName,
    stripPresetConflicts(element.className, undefined, element.designAuthorityClassName),
  );
  const authoredInlineStyle =
    element.style && typeof element.style === "object"
      ? resolveStyleTokens(element.style as React.CSSProperties, designTokens)
      : undefined;
  const toneInlineStyle = buttonToneStyle(tone, designTokens);
  // `tone` is a semantic color-token selection, while `variant` controls the
  // button treatment. Apply the token as a real style so it wins over the
  // structural `.cn-button-variant-*` CSS; an explicitly authored style still
  // has final precedence, matching the Flutter option-resolution contract.
  const inlineStyle =
    toneInlineStyle || authoredInlineStyle
      ? { ...toneInlineStyle, ...authoredInlineStyle }
      : undefined;
  const sourceInlineStyle = isSourceDerivedButton(element)
    ? { ...sourceFillBoxStyle(element, designTokens), ...inlineStyle }
    : inlineStyle;

  const iconEl = IconComponent ? (
    <IconComponent className={element.iconClassName ?? "h-4 w-4"} />
  ) : null;
  const iconRight = element.iconPosition === "right";
  const renderedIconName = IconComponent && !isLoadingBtn
    ? iconName
    : undefined;
  const renderedIconPosition = renderedIconName
    ? iconRight
      ? "right"
      : "left"
    : undefined;

  // Render children as content when no direct text (from rehype pipeline)
  const childContent = hasChildren
    ? element.children!.map((child, idx) => (
        <ElementRenderer key={child.id ?? `child-${idx}`} element={child} />
      ))
    : null;

  if (isSourceDerivedButton(element)) {
    const sourceTag = sourceButtonTag(element);
    const href = element.href
      ? String(resolveSymbol(String(element.href)) ?? "")
      : undefined;
    const rel =
      (element.rel as string | undefined) ??
      (navigationTarget === "_blank" ? "noopener noreferrer" : undefined);
    const sourceClasses = cn(
      element.fill && "w-full",
      toneClassName,
      element.className,
    );
    const sourceContent = (
      <>
        {isLoadingBtn && (
          <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        )}
        {!iconRight && !isLoadingBtn && iconEl}
        {childContent ?? text}
        {iconRight && iconEl}
      </>
    );
    const sourceElementDataAttrs = {
      ...sourceDataAttrs,
      "data-action": href
        ? "navigate"
        : htmlType === "submit"
          ? "submit"
          : "action",
      "data-variant": "source",
      "data-size": "source",
      "data-tone": tone,
      "data-target": navigationTarget,
      "data-icon": renderedIconName,
      "data-icon-position": renderedIconPosition,
    };

    // React Native Pressable/Button sources may legitimately contain nested
    // Text controls with their own tap handlers. A native/ARIA button host
    // would cover those descendants and create invalid nested controls. Keep
    // the parent action on a background proxy and raise real child controls
    // above it, matching the container/card activation contract.
    //
    // Passive subtrees (plain source labels/icons with no events of their own)
    // must NOT take this branch: the source-derived contract renders them as a
    // plain <a>/<button>/<div role="button"> host carrying
    // data-variant="source", the source classes, and the source inline styles
    // (button-atom-defaults + agentic-interactive-attrs contracts).
    if (
      hasChildren &&
      subtreeContainsInteractiveControl(element.children, hasEventBinding)
    ) {
      return (
        <div
          className={cn(sourceClasses, PARENT_ACTIVATION_HOST_CLASSES) || undefined}
          style={parentActivationHostStyle(element, {
            ...sourceInlineStyle,
            pointerEvents: "none",
          })}
          data-cogo-composite-action-host="true"
          {...sourceElementDataAttrs}
        >
          <ParentActivationProxy
            elementId={element.id}
            href={sourceTag === "a" ? href : undefined}
            label={ariaLabel ?? resolveParentActivationLabel(element, resolveSymbol)}
            onClick={handleClick}
          />
          <ParentActivationHostContext.Provider value={true}>
            {sourceContent}
          </ParentActivationHostContext.Provider>
        </div>
      );
    }

    if (sourceTag === "a") {
      return (
        <a
          href={href ?? "#"}
          target={navigationTarget}
          rel={rel}
          aria-label={ariaLabel}
          className={sourceClasses || undefined}
          style={sourceInlineStyle}
          onClick={handleClick}
          {...sourceElementDataAttrs}
        >
          {sourceContent}
        </a>
      );
    }

    if (sourceTag === "button") {
      return (
        <button
          type={htmlType}
          aria-label={ariaLabel}
          className={sourceClasses || undefined}
          style={sourceInlineStyle}
          onClick={handleClick}
          formTarget={navigationTarget}
          disabled={isDisabled || isLoadingBtn}
          {...sourceElementDataAttrs}
        >
          {sourceContent}
        </button>
      );
    }

    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        className={sourceClasses || undefined}
        style={sourceInlineStyle}
        onClick={handleClick}
        {...sourceElementDataAttrs}
      >
        {sourceContent}
      </div>
    );
  }

  // Link button: wrap in <a> if href is provided
  if (element.href) {
    const href = String(resolveSymbol(String(element.href)) ?? "");
    const rel =
      (element.rel as string | undefined) ??
      (navigationTarget === "_blank" ? "noopener noreferrer" : undefined);
    return (
      <a href={href} target={navigationTarget} rel={rel} data-target={navigationTarget}>
        <Button
          variant={variant}
          size={size}
          aria-label={ariaLabel}
          className={extraClasses || undefined}
          style={inlineStyle}
          disabled={isDisabled || isLoadingBtn}
          data-action="navigate"
          data-variant={variant}
          data-size={dataSize}
          data-tone={tone}
          data-target={navigationTarget}
          data-icon={renderedIconName}
          data-icon-position={renderedIconPosition}
          {...sourceDataAttrs}
        >
          {isLoadingBtn && (
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          )}
          {!iconRight && !isLoadingBtn && iconEl}
          {childContent ?? text}
          {iconRight && iconEl}
        </Button>
      </a>
    );
  }

  return (
    <Button
      type={htmlType}
      variant={variant}
      size={size}
      aria-label={ariaLabel}
      className={extraClasses || undefined}
      style={inlineStyle}
      onClick={handleClick}
      formTarget={navigationTarget}
      disabled={isDisabled || isLoadingBtn}
      data-action={htmlType === "submit" ? "submit" : "action"}
      data-variant={variant}
      data-size={dataSize}
      data-tone={tone}
      data-target={navigationTarget}
      data-icon={renderedIconName}
      data-icon-position={renderedIconPosition}
      {...sourceDataAttrs}
    >
      {isLoadingBtn && (
        <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {!iconRight && !isLoadingBtn && iconEl}
      {childContent ?? text}
      {iconRight && iconEl}
    </Button>
  );
}

function buttonToneClassName(tone: string): string | undefined {
  switch (tone) {
    case "destructive":
    case "danger":
    case "error":
      return "border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90";
    case "secondary":
      return "border-secondary bg-secondary text-secondary-foreground hover:bg-secondary/80";
    case "muted":
    case "soft":
      return "border-muted bg-muted text-muted-foreground hover:bg-muted/80";
    case "primary":
      return "border-primary bg-primary text-primary-foreground hover:bg-primary/90";
    default:
      return undefined;
  }
}

const BUILTIN_BUTTON_TONE_TOKENS = new Set([
  "primary",
  "secondary",
  "accent",
  "muted",
  "destructive",
  "success",
  "warning",
  "info",
]);

const BUILTIN_BUTTON_TONE_FOREGROUNDS = new Set([
  "primary",
  "secondary",
  "accent",
  "muted",
  "destructive",
]);

function buttonToneStyle(
  tone: string,
  designTokens: RendererDesignTokens | undefined,
): React.CSSProperties | undefined {
  const token = buttonToneToken(tone);
  if (
    !token ||
    (!BUILTIN_BUTTON_TONE_TOKENS.has(token) &&
      !hasDesignColorToken(designTokens, token))
  ) {
    return undefined;
  }

  const foregroundToken = `${token}-foreground`;
  const hasForeground =
    BUILTIN_BUTTON_TONE_FOREGROUNDS.has(token) ||
    hasDesignColorToken(designTokens, foregroundToken);

  return {
    backgroundColor: resolveColorToken(token, designTokens),
    ...(hasForeground
      ? { color: resolveColorToken(foregroundToken, designTokens) }
      : {}),
  };
}

function buttonToneToken(tone: string): string | undefined {
  switch (tone) {
    case "default":
      return undefined;
    case "danger":
    case "error":
      return "destructive";
    case "soft":
      return "muted";
    default:
      return /^[a-z][a-z0-9._-]*$/i.test(tone) ? tone : undefined;
  }
}

function hasDesignColorToken(
  designTokens: RendererDesignTokens | undefined,
  token: string,
): boolean {
  const colors = designTokens?.colors as Record<string, unknown> | undefined;
  if (!colors) return false;
  const camelToken = token.replace(/-([a-z])/g, (_, char: string) =>
    char.toUpperCase(),
  );
  const value = colors[token] ?? colors[camelToken];
  return typeof value === "string" && value.trim().length > 0;
}

function resolveHtmlButtonType(
  element: RendererProps["element"],
): "button" | "submit" | "reset" {
  // CANONICAL PLACEMENT FIRST. normalizeElement lifts `options.*` and
  // liftV4Props lifts `props.*` onto the element's own top level, so on the
  // ElementRenderer path this one read already covers all three spellings.
  // It also covers the ONLY spelling the generation surface can express:
  // element-types.json declares button's props flat and has no `options` bag
  // for button, and prompt-builders' renderComponentTypes() renders them
  // flat — so reading `options` alone was what made `htmlType` undeclarable
  // (the one spelling the ontology could teach was the one nothing read).
  //
  // The `options` read is kept BEHIND it, not removed: ButtonRenderer is a
  // public export of this package (renderers/index.ts) while normalizeElement
  // is not exported at all, so an external caller rendering it directly gets
  // no normalization pass and the legacy wrapper is its only spelling.
  const flat = (element as unknown as Record<string, unknown>).htmlType;
  const htmlType =
    flat ?? (element.options as Record<string, unknown> | undefined)?.htmlType;
  return htmlType === "submit" || htmlType === "reset" ? htmlType : "button";
}

function isSourceDerivedButton(element: RendererProps["element"]): boolean {
  const options = element.options as Record<string, unknown> | undefined;
  return (
    isSourceDerivedElement(element) ||
    options?.sourceDerived === true ||
    typeof options?.sourceTag === "string"
  );
}

function sourceButtonTag(
  element: RendererProps["element"],
): "a" | "button" | "div" {
  const options = element.options as Record<string, unknown> | undefined;
  const sourceTag =
    typeof options?.sourceTag === "string" ? options.sourceTag : undefined;
  if (sourceTag === "a") return "a";
  if (sourceTag === "button") return "button";
  return "div";
}

function dataAttributesFor(element: RendererProps["element"]) {
  const raw = (
    element as {
      dataAttributes?: Record<
        string,
        string | number | boolean | null | undefined
      >;
    }
  ).dataAttributes;
  const attrs: Record<string, string | number | boolean> = {};

  if (!raw) return attrs;

  for (const [rawKey, value] of Object.entries(raw)) {
    if (value == null) continue;
    const key = rawKey.startsWith("data-")
      ? rawKey
      : `data-${rawKey.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)}`;
    attrs[key] = value;
  }

  return attrs;
}
